import type { FC, ReactNode } from 'react';
import React, { useCallback, useEffect, useId, useRef } from 'react';
import cx from 'classnames';

import { ChevronLeftIcon, CrossIcon } from '@motorway/mw-highway-code';

import styles from './DialogModal.module.scss';

export interface DialogModalClassNames {
	content?: string;
	dialog?: string;
	footer?: string;
	header?: string;
	panel?: string;
}

export interface DialogModalProps {
	backLabel?: string;
	children?: ReactNode;
	classNames?: DialogModalClassNames;
	closeLabel?: string;
	footer?: ReactNode;
	isOpen: boolean;
	onBackButtonClick?: () => void;
	onClose: () => void;
	shouldCloseOnBackdropClick?: boolean;
	showCloseButton?: boolean;
	size?: 'small' | 'large';
	title?: ReactNode;
}

export const DialogModal: FC<DialogModalProps> = ({
	backLabel = 'Back',
	children,
	classNames = {},
	closeLabel = 'Close',
	footer,
	isOpen,
	onBackButtonClick,
	onClose,
	shouldCloseOnBackdropClick = true,
	showCloseButton = true,
	size = 'large',
	title,
}) => {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const titleId = useId();

	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) return;

		if (isOpen && !dialog.open) {
			dialog.showModal();
		} else if (!isOpen && dialog.open) {
			dialog.close();
		}
	}, [isOpen]);

	const handleCancel = useCallback(
		(event: React.SyntheticEvent<HTMLDialogElement>) => {
			event.preventDefault();
			onClose();
		},
		[onClose],
	);

	const handleClose = useCallback(() => {
		if (isOpen) {
			onClose();
		}
	}, [isOpen, onClose]);

	const handleBackdropClick = useCallback(
		(event: React.MouseEvent<HTMLDialogElement>) => {
			if (!shouldCloseOnBackdropClick) return;
			if (event.target === dialogRef.current) {
				onClose();
			}
		},
		[onClose, shouldCloseOnBackdropClick],
	);

	return (
		<dialog
			ref={dialogRef}
			aria-labelledby={title ? titleId : undefined}
			className={cx(styles.dialog, styles[`${size}Size`], classNames.dialog)}
			onCancel={handleCancel}
			onClick={handleBackdropClick}
			onClose={handleClose}
		>
			<div className={cx(styles.panel, classNames.panel)}>
				{onBackButtonClick && (
					<button aria-label={backLabel} className={styles.backButton} onClick={onBackButtonClick} type="button">
						<ChevronLeftIcon />
					</button>
				)}
				{showCloseButton && (
					<button aria-label={closeLabel} className={styles.dismiss} onClick={onClose} type="button">
						<CrossIcon />
					</button>
				)}
				{title && (
					<div className={cx(styles.header, classNames.header)}>
						{typeof title === 'string' ? (
							<h2 className={styles.title} id={titleId}>
								{title}
							</h2>
						) : (
							<div id={titleId}>{title}</div>
						)}
					</div>
				)}
				<div className={cx(styles.content, classNames.content)}>{children}</div>
				{footer && <div className={cx(styles.footer, classNames.footer)}>{footer}</div>}
			</div>
		</dialog>
	);
};
